import React, { useEffect, useState } from "react";
import AdminLayout from "../../layout/AdminLayout";
import AddScreen from "./AddScreen";
import { IoMdTrash } from "react-icons/io";
import { FiEdit } from "react-icons/fi";
import { deleteScreen, getAllScreens } from "../../../api/screens.api";
import { getAllMobileLocations } from "../../../api/mobileLocation.api";
import { confirmScreenDelete, success } from "../../../utils/notifications";
import { RotatingLines } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

const initialData = {
  status: "",
  model: "",
  vehicle: "",
};

// Debounce hook for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function Screens() {
  const [addScreenOpen, setAddScreenOpen] = useState(false);
  const [screensData, setScreensData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalScreens: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    onlineStatus: "all",
    screenType: "all",
    location: "all",
  });
  const [searchInput, setSearchInput] = useState("");

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(searchInput, 500);

  // Fetch locations from mobileLocation API
  const fetchLocations = async () => {
    setLocationsLoading(true);
    try {
      const response = await getAllMobileLocations();
      // Assuming the API returns an array of location objects
      const locationsData = response?.data?.data || response?.data || [];

      // Extract unique location names from the response
      // Since locations are stored as references, we need to get the actual location names
      const uniqueLocations = locationsData
        .filter((location) => location.name) // Filter out locations without names
        .reduce((acc, location) => {
          if (!acc.find((loc) => loc.name === location.name)) {
            acc.push(location);
          }
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically

      setLocations(uniqueLocations);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchScreensData = (page = 1) => {
    setLoading(true);

    const params = {
      page,
      limit: 15,
      ...filters,
      search: debouncedSearch,
    };

    // Remove "all" values from filters
    Object.keys(params).forEach((key) => {
      if (params[key] === "all") {
        delete params[key];
      }
    });

    getAllScreens(params)
      .then((res) => {
        setLoading(false);
        setScreensData(res?.data?.screens || []);
        setPagination(
          res?.data?.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalScreens: 0,
            hasNextPage: false,
            hasPrevPage: false,
          }
        );
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching screens data:", error);
      });
  };

  useEffect(() => {
    fetchScreensData(1);
    fetchLocations();
  }, [filters, debouncedSearch]);

  const handleDelete = (id) => {
    confirmScreenDelete().then(async (result) => {
      if (result.isConfirmed) {
        await deleteScreen(id);
        setScreensData(screensData.filter((screen) => screen._id !== id));
        await success("Success!", "Screen deleted successfully!");
        fetchScreensData(pagination.currentPage);
      }
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchScreensData(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      onlineStatus: "all",
      screenType: "all",
      location: "all",
    });
    setSearchInput("");
  };

  // Function to truncate long text with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "-";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Helper function to get location name
  const getLocationName = (location) => {
    return location?.name || "Unknown Location";
  };

  // Helper function to get screen location for display
  const getScreenLocation = (screen) => {
    if (screen.screenType === "vehicle") {
      return screen.vehicle?.locationName || screen.vehicle?.location || "-";
    } else if (screen.screenType === "restaurant") {
      return (
        screen.restaurant?.locationName || screen.restaurant?.location || "-"
      );
    }
    return "-";
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(
      1,
      pagination.currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(
      pagination.totalPages,
      startPage + maxVisiblePages - 1
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <>
      <AddScreen
        open={addScreenOpen}
        setOpen={setAddScreenOpen}
        fetchScreensData={() => fetchScreensData(pagination.currentPage)}
        initialData={initialData}
      />
      <AdminLayout title="Screens">
        {/* Filters Section */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6 ms-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Screen
              </label>
              <input
                type="text"
                placeholder="Search by screen name..."
                value={searchInput}
                onChange={handleSearchChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Screen Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="In Active">In Active</option>
                <option value="Under Maintenance">Under Maintenance</option>
              </select>
            </div>

            {/* Display Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Status
              </label>
              <select
                value={filters.onlineStatus}
                onChange={(e) =>
                  handleFilterChange("onlineStatus", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Display Status</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="No Internet">No Internet</option>
              </select>
            </div>

            {/* Screen Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Screen Type
              </label>
              <select
                value={filters.screenType}
                onChange={(e) =>
                  handleFilterChange("screenType", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="vehicle">Mobile Locations</option>
                <option value="restaurant">Fixed Locations</option>
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <select
                value={filters.location}
                onChange={(e) => handleFilterChange("location", e.target.value)}
                disabled={locationsLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="all">All Locations</option>
                {locationsLoading ? (
                  <option value="" disabled>
                    Loading locations...
                  </option>
                ) : (
                  locations.map((location, index) => (
                    <option key={location._id || index} value={location.name}>
                      {getLocationName(location)}
                    </option>
                  ))
                )}
              </select>
              {locationsLoading && (
                <div className="mt-1">
                  <RotatingLines
                    strokeColor="gray"
                    strokeWidth="3"
                    animationDuration="0.75"
                    width="20"
                    visible={true}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="flex justify-between items-center">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Filters
            </button>

            {/* Results Count */}
            <div className="text-sm text-gray-600">
              Showing {screensData.length} of {pagination.totalScreens} screens
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <RotatingLines
              strokeColor="black"
              strokeWidth="5"
              animationDuration="0.75"
              width="96"
              visible={true}
            />
          </div>
        ) : (
          <div className="mx-2 sm:mx-4 lg:mx-8">
            {/* Empty state */}
            {screensData.length === 0 && (
              <div className="text-center py-10">
                <p className="text-gray-500 text-lg">No screens found</p>
                {(filters.search ||
                  filters.status !== "all" ||
                  filters.onlineStatus !== "all" ||
                  filters.screenType !== "all" ||
                  filters.location !== "all") && (
                  <button
                    onClick={clearFilters}
                    className="mt-2 px-4 py-2 text-blue-600 hover:text-blue-800"
                  >
                    Clear filters to see all screens
                  </button>
                )}
              </div>
            )}

            {/* Screens Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {screensData.map((screen, index) => (
                <div
                  key={screen._id}
                  className="cursor-pointer transition-all duration-300 hover:shadow-lg"
                  onClick={() => navigate(`/screendetails/${screen._id}`)}
                >
                  <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col border border-gray-100">
                    {/* Header with status */}
                    <div className="px-4 py-3 flex justify-between items-center bg-yellow-500">
                      <h3 className="font-semibold text-gray-800">
                        {truncateText(screen?.model, 25)}
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(screen._id);
                          }}
                          className="text-gray-600 hover:text-red-600"
                        >
                          <IoMdTrash size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Main content */}
                    <div className="p-4 flex-1">
                      {/* Status indicators */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Screen Status</p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              screen?.status === "Active"
                                ? "bg-green-100 text-green-800"
                                : screen?.status === "In Active"
                                ? "bg-orange-100 text-orange-800"
                                : screen?.status === "Under Maintenance"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {screen?.status || "Unknown"}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            Display Status
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
      ${
        screen?.onlineStatus?.toLowerCase() === "online"
          ? "bg-green-100 text-green-800"
          : screen?.onlineStatus?.toLowerCase() === "offline"
          ? "bg-red-100 text-red-800"
          : screen?.onlineStatus?.toLowerCase() === "no internet"
          ? "bg-yellow-100 text-yellow-800"
          : "bg-gray-100 text-gray-800"
      }`}
                          >
                            {screen?.onlineStatus || "Unknown"}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between items-start mb-4">
                        {/* Screen Type */}
                        <div>
                          <p className="text-xs text-gray-500">Screen Type</p>
                          <p className="text-sm font-medium text-gray-900">
                            {screen?.screenType === "vehicle"
                              ? "Mobile Location"
                              : "Fixed Location"}
                          </p>
                        </div>

                        {/* Location */}
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Location</p>
                          <p className="text-sm font-medium text-gray-900">
                            {getScreenLocation(screen)}
                          </p>
                        </div>
                      </div>

                      {/* Last Connection */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500">Last Connection</p>
                        {screen?.lastConnection ? (
                          <>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDistanceToNow(
                                new Date(screen.lastConnection),
                                { addSuffix: true }
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(screen.lastConnection).toLocaleString(
                                "en-US",
                                {
                                  weekday: "short",
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm font-medium text-gray-900">-</p>
                        )}
                      </div>

                      {/* Dynamic content based on screen type */}
                      <div className="border-t border-gray-100 pt-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          {screen?.screenType === "vehicle"
                            ? "Vehicle Details"
                            : screen?.screenType === "restaurant"
                            ? "Restaurant Details"
                            : "Details"}
                        </h4>

                        {screen?.screenType === "vehicle" && (
                          <div className="space-y-2">
                            {[
                              { label: "Brand", value: screen?.vehicle?.brand },
                              { label: "Model", value: screen?.vehicle?.model },
                              { label: "Type", value: screen?.vehicle?.type },
                            ].map((item, i) => (
                              <div key={i}>
                                <p className="text-xs text-gray-500">
                                  {item.label}
                                </p>
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {truncateText(item.value, 30) || "-"}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {screen?.screenType === "restaurant" && (
                          <div className="space-y-2">
                            {[
                              {
                                label: "Name",
                                value: screen?.restaurant?.name,
                              },
                              {
                                label: "Cuisine",
                                value: screen?.restaurant?.cuisineType,
                              },
                              {
                                label: "Capacity",
                                value: screen?.restaurant?.seatingCapacity,
                              },
                              {
                                label: "Phone",
                                value:
                                  screen?.restaurant?.contactInformation?.phone,
                              },
                            ].map((item, i) => (
                              <div key={i}>
                                <p className="text-xs text-gray-500">
                                  {item.label}
                                </p>
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {item.label === "Phone" ||
                                  item.label === "Capacity"
                                    ? item.value || "-"
                                    : truncateText(item.value, 30) || "-"}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 py-4">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`px-3 py-2 rounded-md ${
                    pagination.hasPrevPage
                      ? "bg-gray-200 hover:bg-gray-300 text-gray-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded-md ${
                      pageNum === pagination.currentPage
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`px-3 py-2 rounded-md ${
                    pagination.hasNextPage
                      ? "bg-gray-200 hover:bg-gray-300 text-gray-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </AdminLayout>
    </>
  );
}

export default Screens;
